// This file centralizes access to environment variables
//
// It reads from three sources, in order of precedence:
//  1. window.env (PRODUCTION)
//     Set by the server at runtime, allows for dynamic configuration without rebuilding the app
//
//     This is populated in the index.html of each app using template variables and docker build args.
//     See Dockerfile for details.
//
//  2. import.meta.env (Optional, DEVELOPMENT)
//     In case you want to set environment variables during development without modifying the index.html,
//     you can create a <app>/.env file in the root of the app with VITE_ prefix, e.g. VITE_API_URL=http://localhost:9000
//
//  3. default values
//     used if neither window.env nor import.meta.env provide a value
//     For your comfort.
import pkg from "../../package.json" with { type: "json" };

/**
 * Returns values from window.env if available and templated (not equal to ${VAR_NAME})
 */
function windowEnv(name) {
  const value = window?.env?.[name];
  if (typeof value === "string" && value !== `\${${name}}`) {
    return value;
  }
  return undefined;
}

const ENVIRONMENT =
  windowEnv("ENVIRONMENT") || import.meta.env.VITE_ENVIRONMENT || "DEVELOPMENT";

// Identifies which app is currently running: "HORSES" | "PIGS" | "SMALL_LIVESTOCK" | "CATTLE"
// Set as a literal (non-templated) value per app in index.html, since it never
// varies across deployment environments (dev/staging/production).
const APP_MODULE =
  windowEnv("APP_MODULE") || import.meta.env.VITE_APP_MODULE || "UNKNOWN";

const env = Object.freeze({
  APP_VERSION:
    windowEnv("APP_VERSION") ||
    import.meta.env.VITE_APP_VERSION ||
    pkg?.version ||
    "0.0.0",

  APP_BUILD_DATE:
    windowEnv("APP_BUILD_DATE") ||
    import.meta.env.VITE_APP_BUILD_DATE ||
    "hotreload-" + new Date().toISOString(),

  API_URL:
    windowEnv("API_URL") ||
    import.meta.env.VITE_API_URL ||
    "http://localhost:9000",

  APP_URL_SMALL_LIVESTOCK:
    windowEnv("APP_URL_SMALL_LIVESTOCK") ||
    import.meta.env.VITE_APP_URL_SMALL_LIVESTOCK ||
    "http://localhost:3000",

  APP_URL_PIGS:
    windowEnv("APP_URL_PIGS") ||
    import.meta.env.VITE_APP_URL_PIGS ||
    "http://localhost:3001",

  APP_URL_HORSES:
    windowEnv("APP_URL_HORSES") ||
    import.meta.env.VITE_APP_URL_HORSES ||
    "http://localhost:3002",

  APP_URL_CATTLE:
    windowEnv("APP_URL_CATTLE") ||
    import.meta.env.VITE_APP_URL_CATTLE ||
    "https://e.zivinoreja.si/govedo/index.php",

  // The standalone "PV Govedo" app (govedo-ui) — breeding values for cattle only.
  // Deliberately separate from APP_URL_CATTLE, which still points at the legacy
  // eGovedo system linked from the module dropdown.
  APP_URL_GOVEDO_PV:
    windowEnv("APP_URL_GOVEDO_PV") ||
    import.meta.env.VITE_APP_URL_GOVEDO_PV ||
    "http://localhost:3003",

  APP_URL_BEES:
    windowEnv("APP_URL_BEES") ||
    import.meta.env.VITE_APP_URL_BEES ||
    "https://e.zivinoreja.si/cebele/index.php",

  APP_URL_GENE_BANK:
    windowEnv("APP_URL_GENE_BANK") ||
    import.meta.env.VITE_APP_URL_GENE_BANK ||
    "http://gb.bf.uni-lj.si/genebank/",

  APP_URL_LANDING_PAGE:
    windowEnv("APP_URL_LANDING_PAGE") ||
    import.meta.env.VITE_APP_URL_LANDING_PAGE ||
    "https://e.zivinoreja.si",

  APP_AUTHOR:
    windowEnv("APP_AUTHOR") ||
    import.meta.env.VITE_APP_AUTHOR ||
    "@2026 Univerza v Ljubljani",

  OIDC_AUTHORITY:
    windowEnv("OIDC_AUTHORITY") ||
    import.meta.env.VITE_OIDC_AUTHORITY ||
    "https://dev-auth.pokk.result.si/realms/pokk",

  OIDC_CLIENT_ID:
    windowEnv("OIDC_CLIENT_ID") ||
    import.meta.env.VITE_OIDC_CLIENT_ID ||
    "pokk-ui",

  OPENSEARCH_URL:
    windowEnv("OPENSEARCH_URL") ||
    import.meta.env.VITE_OPENSEARCH_URL ||
    "http://10.10.10.88:5601/goto/cc7313afe5cb04fb690887f0bbf76e3b?security_tenant=global",

  GRAFANA_URL:
    windowEnv("GRAFANA_URL") ||
    import.meta.env.VITE_GRAFANA_URL ||
    "http://10.10.10.88:8000",

  APP_ZOOTECHNICAL_CERTIFICATE_SMALL_LIVESTOCK_JASPER_REPORT_UUID:
    windowEnv(
      "APP_ZOOTECHNICAL_CERTIFICATE_SMALL_LIVESTOCK_JASPER_REPORT_UUID"
    ) ||
    import.meta.env
      .VITE_APP_ZOOTECHNICAL_CERTIFICATE_SMALL_LIVESTOCK_JASPER_REPORT_UUID ||
    "dd4d70b3-8c8d-4958-9762-846d07c216bf",

  BASE_PATH: windowEnv("BASE_PATH") || import.meta.env.BASE_URL || "/",

  ENVIRONMENT,

  isDevelopment: ENVIRONMENT.toUpperCase() === "DEVELOPMENT",
  isProduction: ENVIRONMENT.toUpperCase() === "PRODUCTION",

  APP_MODULE,

  isHorses: APP_MODULE === "HORSES",
  isPigs: APP_MODULE === "PIGS",
  isSmallLivestock: APP_MODULE === "SMALL_LIVESTOCK",
  isCattle: APP_MODULE === "CATTLE",
});

export default env;
